/**
 * Runtime configuration.
 *
 * Vite inlines `import.meta.env` at build time, which meant the API URL was compiled into the
 * bundle and a non-prod build could never become a prod build. Every environment needed its own
 * artifact, so "promote the thing that passed testing" was not possible for the admin console
 * the way it is for the services.
 *
 * Now the values come from `/config.json`, fetched once before the app mounts and written per
 * bucket by CI. One bundle, promoted unchanged. The build-time values remain as the fallback so
 * `npm run dev` keeps working from `.env.local` with no server involved.
 */

export type AppEnv = 'local' | 'nonprod' | 'prod';

type RuntimeConfig = {
  apiBaseUrl: string;
  appEnv: AppEnv;
  mapsApiKey: string;
};

const buildTimeDefaults: RuntimeConfig = {
  apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080',
  appEnv: (import.meta.env.VITE_APP_ENV ?? 'local') as AppEnv,
  mapsApiKey: import.meta.env.VITE_MAPS_API_KEY ?? '',
};

let resolved: RuntimeConfig = buildTimeDefaults;

/**
 * Fetch `/config.json` and merge it over the build-time defaults.
 *
 * Must be awaited before anything reads `env` — `src/api/client.ts` calls `axios.create` at
 * module scope, so the baseURL is captured the moment that module first evaluates. `main.tsx`
 * therefore loads the config and only then dynamically imports the app.
 *
 * A missing or unreadable config.json is not fatal: the app falls back to the build-time values
 * and logs. In development that is the normal case, and in production a hard failure here would
 * turn a cache miss into a blank page.
 */
export async function loadRuntimeConfig(): Promise<void> {
  try {
    // cache: 'no-store' matters. config.json is the one file that must not be served stale
    // after a promotion, and CloudFront will happily hold onto it otherwise.
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const loaded = (await res.json()) as Partial<RuntimeConfig>;
    // A blank value means "not set here", not "set to nothing": the dev copy of config.json
    // ships `"mapsApiKey": ""`, which used to wipe the key from .env.local and leave the
    // landmark map picker disabled in every local run.
    const present = Object.fromEntries(
      Object.entries(loaded).filter(([, v]) => v !== '' && v !== null && v !== undefined),
    ) as Partial<RuntimeConfig>;
    resolved = { ...buildTimeDefaults, ...present };
  } catch (err) {
    console.warn(
      '[env] /config.json unavailable, using build-time defaults:',
      err instanceof Error ? err.message : err,
    );
  }
}

export const env = {
  get apiBaseUrl() {
    return resolved.apiBaseUrl;
  },
  get appEnv() {
    return resolved.appEnv;
  },
  get mapsApiKey() {
    return resolved.mapsApiKey;
  },
} as const;
